
import { catchError, map, mergeMap, toArray } from 'rxjs/operators';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription, from } from 'rxjs';
import { Debounce } from 'lodash-decorators';
import { MessageService } from 'primeng/api';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { MailingListsService } from '../../services/mailing-lists.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { MailingListModel } from '../../models/mailingList.model';
import { RecipientModel } from '../../models/recipient.model';
import { TextInputDialogComponent } from '../../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '../../../Dialogs/models/text-input-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { EditRecipientDialogComponent } from '../EditRecipientDialog/edit-recipient-dialog.component';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { ImportRecipientsDialogComponent } from '../ImportRecipientsDialog/import-recipients-dialog.component';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BriefingsApiErrorEnum } from '../../../../api/briefings/enum/briefings-api-error.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { BulkActionConfirmationDialogComponent } from '@Common/Dialogs/components/BulkActionConfirmationDialog/bulk-action-confirmation-dialog.component';

@Component({
    selector: 'agpr-manage-mailing-lists-dialog',
    templateUrl: './manage-mailing-lists-dialog.component.html',
    styleUrls: ['./manage-mailing-lists-dialog.component.scss'],
    standalone: false
})
export class ManageMailingListsDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    public readonly buttonTypes = ButtonTypesEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;

    private subscriptions: Subscription[] = [];

    public mailingListsSearch: string = '';
    public mailingLists: MailingListModel[] = [];
    public filteredMailingLists: MailingListModel[] = [];
    public selectedMailingList: MailingListModel;

    public recipientsInListSearch: string = '';
    public recipientsInList: RecipientModel[] = [];
    public filteredRecipientsInList: RecipientModel[] = [];
    public recipientsInListRequest: Subscription;

    public allRecipientsSearch: string = '';
    public allRecipients: RecipientModel[] = [];
    public allRecipientsTotal: number | null = null;
    public allRecipientsListComplete: boolean = false;
    public allRecipientsRequest: Subscription;
    public selectedRecipientIds: number[] = [];
    public recipientIdsInList = new Set<number>();
    public allSelected: boolean = false;
    public selectAllRequest: Subscription;
    public selectedInListIds: number[] = [];
    public isAllSelectedInList: boolean = true;

    private readonly recipientSearchPageSize: number = 25;
    private readonly MAX_IDS_PER_DELETE = 100;

    constructor(
        public coreDialogService: CoreDialogService,
        private mailingListsService: MailingListsService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'ManageMailingLists');
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.mailingListsService.getMailingLists().subscribe(
                mailingLists => {
                    if (mailingLists) {
                        this.mailingLists = mailingLists.sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}));
                    } else {
                        this.mailingLists = null;
                    }
                    this.filterMailingLists();

                    if (this.mailingLists) {
                        let newSelectedList: MailingListModel;
                        if (this.selectedMailingList) {
                            newSelectedList = this.mailingLists.find(m => this.selectedMailingList.id === m.id);
                        }
                        // take the new one, or the first from the filtered list or global list
                        this.selectMailingList(newSelectedList || this.filteredMailingLists[0] || this.mailingLists[0]);
                    }
                }
            )
        );

        this.filterAllRecipients();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions.length = 0;

        if (this.recipientsInListRequest) {
            this.recipientsInListRequest.unsubscribe();
            this.recipientsInListRequest = null;
        }
        if (this.allRecipientsRequest) {
            this.allRecipientsRequest.unsubscribe();
            this.allRecipientsRequest = null;
        }
        if (this.selectAllRequest) {
            this.selectAllRequest.unsubscribe();
            this.selectAllRequest = null;
        }
    }

    selectMailingList(mailingList: MailingListModel, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        if (mailingList && this.selectedMailingList && mailingList.id === this.selectedMailingList.id) {
            // if the recipients are the same, then we don't need to go refetching them
            if (
                !mailingList.recipientIds.find(newId => !this.selectedMailingList.recipientIds.includes(newId))
                && !this.selectedMailingList.recipientIds.find(newId => !mailingList.recipientIds.includes(newId))
            ) {
                this.selectedMailingList = mailingList;
                return;
            }
        }
        this.selectedMailingList = mailingList;
        if (!mailingList) {
            return;
        }
        this.selectedInListIds = [];
        this.refreshSelectedListRecipientIds();

        if (this.recipientsInListRequest) {
            this.recipientsInListRequest.unsubscribe();
            this.recipientsInListRequest = null;
        }

        this.recipientsInList = [];
        this.filteredRecipientsInList = [];
        if (!this.selectMailingList || this.selectedMailingList.recipientIds.length < 1) {
            return;
        }

        this.recipientsInListRequest = this.mailingListsService.getRecipientsForMailingList(mailingList).subscribe(
            recipients => {
                this.recipientsInList = recipients;
                this.selectedMailingList.recipientIds = recipients.map(r => r.id);
                this.refreshSelectedListRecipientIds();
                this.filterRecipientsInList();
                this.recipientsInListRequest.unsubscribe();
                this.recipientsInListRequest = null;
            }
        );
    }

    filterMailingLists(): void {
        if (!this.mailingLists) {
            this.filteredMailingLists = [];
            return;
        }
        if (!this.mailingListsSearch) {
            this.filteredMailingLists = this.mailingLists.slice();
            return;
        }

        const queryLower = this.mailingListsSearch.toLocaleLowerCase();
        this.filteredMailingLists = this.mailingLists.filter(mailingList => {
            return mailingList.name.toLocaleLowerCase().includes(queryLower);
        });
    }
    filterRecipientsInList(): void {
        if (!this.recipientsInList) {
            return;
        }
        if (!this.recipientsInListSearch) {
            this.filteredRecipientsInList = this.recipientsInList.slice();
            return;
        }

        const queryLower = this.recipientsInListSearch.toLocaleLowerCase();
        this.filteredRecipientsInList = this.recipientsInList.filter(recipient => {
            if (recipient.name && recipient.name.toLocaleLowerCase().includes(queryLower)) {
                return true;
            }
            return recipient.email.toLocaleLowerCase().includes(queryLower);
        });
    }

    @Debounce(300, {leading: false})
    filterAllRecipients(): void {
        if (this.allRecipientsRequest) {
            this.allRecipientsRequest.unsubscribe();
            this.allRecipientsRequest = null;
        }
        this.allSelected = false;

        this.allRecipients = [];
        this.allRecipientsRequest = this.mailingListsService.searchRecipients(this.allRecipientsSearch, 0, this.recipientSearchPageSize).subscribe(
            results => {
                this.allRecipients = results.recipients;
                this.allRecipientsTotal = results.totalCount;
                this.allRecipientsListComplete = this.allRecipients.length >= results.totalCount;
                this.allRecipientsRequest.unsubscribe();
                this.allRecipientsRequest = null;
            }
        );
    }

    getMoreAllRecipients(): void {
        if (this.allRecipientsListComplete) {
            return;
        }
        if (this.allRecipientsRequest) {
            return;
        }

        const page = Math.ceil(this.allRecipients.length / this.recipientSearchPageSize);
        this.allRecipientsRequest = this.mailingListsService.searchRecipients(this.allRecipientsSearch, page, this.recipientSearchPageSize).subscribe(
            results => {
                this.allRecipients = [
                    ...this.allRecipients,
                    ...results.recipients
                ];
                this.allRecipientsTotal = results.totalCount;
                this.allRecipientsListComplete = this.allRecipients.length >= results.totalCount;
                this.allRecipientsRequest.unsubscribe();
                this.allRecipientsRequest = null;
            }
        );
    }

    createMailingList(): void {
        this.coreDialogService.open(
            TextInputDialogComponent,
            <TextInputDialogModel>{
                inputs: {
                    heading: 'Common.mailingLists.createListDialog.heading',
                    description: 'Common.mailingLists.createListDialog.description',
                    placeholder: 'Common.mailingLists.createListDialog.name.placeholder',
                    okLabel: 'Common.mailingLists.createListDialog.ok',
                    maxLength: 32,
                    width: 300,
                    disabledButtonTooltip: 'Common.mailingLists.createListDialog.name.required',
                    submit: (name: string) => {
                        const mailingList = new MailingListModel();
                        mailingList.name = name;

                        return this.mailingListsService.createMailingList(mailingList).pipe(
                            map((createdList: MailingListModel) => {
                                const selectedList = this.mailingLists?.find(m => m.id === createdList.id);
                                this.selectMailingList(selectedList || createdList);
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'Common.mailingLists.createListDialog.success.heading',
                                    detail: this.localizationService.transform(
                                        'Common.mailingLists.createListDialog.success.message',
                                        { name: mailingList.name }
                                    )
                                });
                            }),
                            catchError((err) => {
                                let message = 'Common.mailingLists.createListDialog.error.message';

                                if (
                                    err
                                    && err.error
                                    && err.error.details
                                    && err.error.details[0]
                                    && err.error.details[0].code === BriefingsApiErrorEnum.mailingListNameDuplicate
                                ) {
                                    message = 'Common.mailingLists.createListDialog.error.message.duplicate';
                                }
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Common.mailingLists.createListDialog.error.heading',
                                    detail: this.localizationService.transform(
                                        message,
                                        {
                                            name: mailingList.name
                                        }
                                    )
                                });
                                throw err;
                            })
                        );
                    }
                }
            }
        );
    }
    editMailingList(mailingList: MailingListModel): void {
        this.coreDialogService.open(
            TextInputDialogComponent,
            <TextInputDialogModel>{
                inputs: {
                    heading: 'Common.mailingLists.editListDialog.heading',
                    description: 'Common.mailingLists.editListDialog.description',
                    placeholder: 'Common.mailingLists.editListDialog.name.placeholder',
                    okLabel: 'Common.mailingLists.editListDialog.ok',
                    maxLength: 32,
                    width: 300,
                    initialValue: mailingList.name,
                    submit: (name: string) => {
                        const updatedList = mailingList.clone();
                        updatedList.name = name;

                        return this.mailingListsService.updateMailingList(updatedList).pipe(
                            map(() => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'Common.mailingLists.editListDialog.success.heading',
                                    detail: this.localizationService.transform(
                                        'Common.mailingLists.editListDialog.success.message',
                                        {
                                            name: updatedList.name
                                        }
                                    )
                                });
                            }),
                            catchError((err) => {
                                let message = 'Common.mailingLists.editListDialog.error.message';

                                if (
                                    err
                                    && err.error
                                    && err.error.details
                                    && err.error.details[0]
                                    && err.error.details[0].code === BriefingsApiErrorEnum.mailingListNameDuplicate
                                ) {
                                    message = 'Common.mailingLists.editListDialog.error.message.duplicate';
                                }
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Common.mailingLists.editListDialog.error.heading',
                                    detail: this.localizationService.transform(
                                        message,
                                        {
                                            name: updatedList.name
                                        }
                                    )
                                });
                                throw err;
                            })
                        );
                    }
                }
            }
        );
    }
    deleteMailingList(mailingList: MailingListModel): void {
        this.coreDialogService.open(
            ConfirmDialogComponent,
            <ConfirmDialogModel>{
                inputs: {
                    heading: 'Common.mailingLists.deleteListDialog.heading',
                    okLabel: 'Common.mailingLists.deleteListDialog.ok',
                    message: {
                        key: 'Common.mailingLists.deleteListDialog.message',
                        args: {
                            name: mailingList.name
                        }
                    },
                    alertBelowMessage: true,
                    alertMessage: 'Common.mailingLists.deleteListDialog.alertMessage',
                    alertType: messageSeverityEnum.warning,
                    confirmAction: () => {
                        return this.mailingListsService.deleteRecipientsByMailingList(mailingList.id).pipe(
                            mergeMap(() => this.mailingListsService.deleteMailingList(mailingList)),
                            map(() => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'Common.mailingLists.deleteListDialog.success.heading',
                                    detail: this.localizationService.transform(
                                        'Common.mailingLists.deleteListDialog.success.message',
                                        { name: mailingList.name }
                                    )
                                });
                                const deletedIds = new Set(mailingList.recipientIds ?? []);
                                this.selectedRecipientIds = this.selectedRecipientIds.filter(id => !deletedIds.has(id));
                                this.filterAllRecipients();
                            }),
                            catchError((err) => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Common.mailingLists.deleteListDialog.error.heading',
                                    detail: this.localizationService.transform(
                                        'Common.mailingLists.deleteListDialog.error.message',
                                        { name: mailingList.name }
                                    )
                                });
                                throw err;
                            })
                        );
                    }
                },
                outputs: {
                    dialogClose: () => {}
                }
            }
        );
    }

    exportMailingList(): void {
        this.mailingListsService.exportMailingList(this.selectedMailingList);
    }

    createRecipient(): void {
        this.coreDialogService.open(
            EditRecipientDialogComponent,
            <DialogIoModel>{
                outputs: {
                    dialogClose: (recipient: RecipientModel) => {
                        if (!recipient) {
                            return;
                        }
                        this.filterAllRecipients();
                    }
                }
            }
        );
    }
    editRecipient(recipient: RecipientModel): void {
        this.coreDialogService.open(
            EditRecipientDialogComponent,
            <DialogIoModel>{
                inputs: {
                    recipient: recipient
                },
                outputs: {
                    dialogClose: (updatedRecipient: RecipientModel) => {
                        if (!updatedRecipient) {
                            return;
                        }
                        const recipientInList = this.recipientsInList.find(r => r.id === updatedRecipient.id);
                        if (recipientInList) {
                            this.filterRecipientsInList();
                        }
                        this.filterAllRecipients();
                    }
                }
            }
        );
    }

    removeRecipientFromList(recipient: RecipientModel): void {
        if (!this.selectedMailingList) {
            return;
        }

        const mailingList = this.selectedMailingList.clone();
        mailingList.recipientIds = this.selectedMailingList.recipientIds.filter(id => id !== recipient.id);

        this.mailingListsService.updateMailingList(mailingList, true).subscribe({
            next: () => {
                this.selectedMailingList.recipientIds = this.selectedMailingList.recipientIds.filter(id => id !== recipient.id);
                this.recipientsInList = this.recipientsInList.filter(r => r.id !== recipient.id);
                this.filterRecipientsInList();
                this.refreshSelectedListRecipientIds();

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Common.mailingLists.manageDialog.removeRecipientFromList.success.heading',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.removeRecipientFromList.success.message',
                        {
                            name: recipient.name || recipient.email,
                            list: this.selectedMailingList.name
                        }
                    )
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.mailingLists.manageDialog.removeRecipientFromList.error.heading',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.removeRecipientFromList.error.message',
                        {
                            name: recipient.name || recipient.email,
                            list: this.selectedMailingList.name
                        }
                    )
                });
            }
        });
    }

    addRecipientToSelectedList(recipient: RecipientModel): void {
        if (!this.selectedMailingList) {
            return;
        }
        if (this.selectedMailingList.recipientIds.includes(recipient.id)) {
            return;
        }
        this.selectedMailingList.recipientIds.push(recipient.id);
        this.recipientsInListUpdated(recipient);
    }

    recipientsInListUpdated(recipient: RecipientModel): void {
        if (!this.selectedMailingList) {
            return;
        }
        const wasAdded = !!this.selectedMailingList.recipientIds.find(id => recipient.id === id);
        const growlPrefix = wasAdded ? 'Common.mailingLists.manageDialog.addRecipientToList' : 'Common.mailingLists.manageDialog.removeRecipientFromList';

        this.mailingListsService.updateMailingList(this.selectedMailingList, !wasAdded).subscribe({
            next: () => {
                if (wasAdded) {
                    this.recipientsInList = [...this.recipientsInList, recipient];
                } else {
                    this.recipientsInList = this.recipientsInList.filter(r => r.id !== recipient.id);
                }
                this.filterRecipientsInList();
                this.refreshSelectedListRecipientIds();

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: `${growlPrefix}.success.heading`,
                    detail: this.localizationService.transform(
                        `${growlPrefix}.success.message`,
                        {
                            name: recipient.name || recipient.email,
                            list: this.selectedMailingList.name
                        }
                    )
                });
            },
            error: (err) => {
                // revert selection/deselection as an error has occured
                if (wasAdded) {
                    this.selectedMailingList.recipientIds = this.selectedMailingList.recipientIds.filter(id => id !== recipient.id);
                } else {
                    this.selectedMailingList.recipientIds.push(recipient.id);
                }
                this.refreshSelectedListRecipientIds();

                switch (err?.error?.details?.[0]?.code) {
                    // only triggered onAdd
                    case BriefingsApiErrorEnum.invalidEmail:
                        // recipient being added has invalid email
                        if (err?.error?.details?.[0]?.invalidValue === recipient.email) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: `${growlPrefix}.error.heading`,
                                detail: this.localizationService.transform(
                                    'Common.mailingLists.manageDialog.error.invalidEmail',
                                    { name: recipient.name || recipient.email }
                                )
                            });
                        } else {
                            // mailingList contains email(s) that are invalid
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: `${growlPrefix}.error.heading`,
                                detail: this.localizationService.transform(
                                    'Common.mailingLists.manageDialog.error.invalidEmail2',
                                    { list: this.selectedMailingList.name }
                                )
                            });
                        }
                        break;
                    default:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: `${growlPrefix}.error.heading`,
                            detail: this.localizationService.transform(
                                `${growlPrefix}.error.message`,
                                {
                                    name: recipient.name || recipient.email,
                                    list: this.selectedMailingList.name
                                }
                            )
                        });
                        break;
                }

            }
        });
    }


    importRecipients(): void {
        this.coreDialogService.open(
            ImportRecipientsDialogComponent,
            {
                outputs: {
                    dialogClose: (didImport: boolean) => {
                        if (didImport) {
                            this.filterRecipientsInList();
                            this.filterAllRecipients();
                        }
                    }
                }
            }
        );
    }


    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    selectAll(): void {
        if (this.selectAllRequest) {
            this.selectAllRequest.unsubscribe();
        }
        this.selectAllRequest = this.mailingListsService
            .searchRecipients(this.allRecipientsSearch, 0, 1).pipe(
                mergeMap(countResult =>
                    this.mailingListsService.searchRecipients(this.allRecipientsSearch, 0, countResult.totalCount)
                )
            ).subscribe({
                next: results => {
                    this.selectedRecipientIds = results.recipients.map(recipient => recipient.id);
                    this.allSelected = true;
                    this.updateIsAllSelectedInList();
                    this.selectAllRequest = null;
                },
                error: () => {
                    this.selectAllRequest = null;
                }
            });
    }

    unselectAll(): void {
        this.allSelected = false;
        this.selectedRecipientIds = [];
        this.updateIsAllSelectedInList();
    }

    onRecipientSelectionChange(): void {
        if (this.allSelected && this.allRecipientsTotal !== null && this.selectedRecipientIds.length < this.allRecipientsTotal) {
            this.allSelected = false;
        }
        this.updateIsAllSelectedInList();
    }

    refreshSelectedListRecipientIds(): void {
        this.recipientIdsInList = new Set(this.selectedMailingList?.recipientIds ?? []);
        this.updateIsAllSelectedInList();
    }

    deleteRecipients(): void {
        const count = this.allSelected ? this.allRecipientsTotal : this.selectedRecipientIds.length;
        if (!count) {
            return;
        }
        this.coreDialogService.open(
            BulkActionConfirmationDialogComponent,
            {
                inputs: {
                    dialogHeading: 'Common.mailingLists.manageDialog.confirmAction.title',
                    message: 'Common.mailingLists.manageDialog.confirmAction.confirmMessage',
                    itemCountLabel: 'Common.mailingLists.manageDialog.confirmAction.contactsSelected',
                    okLabel: 'Common.mailingLists.deleteListDialog.ok',
                    itemCount: count,
                    showTimeWarning: false
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (!confirmed) {
                            return;
                        }
                        this.doBulkDelete(count);
                    }
                }
            }
        );
    }

    private doBulkDelete(count: number): void {
        const deleteAll = this.allSelected && !this.allRecipientsSearch;
        const request$ = deleteAll ? this.mailingListsService.deleteAllRecipients() : this.deleteInBatches(this.selectedRecipientIds);

        request$.subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Common.mailingLists.manageDialog.confirmAction.confirmMessage.success',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.confirmAction.confirmMessage.success.detail',
                        { count }
                    )
                });

                this.allSelected = false;
                this.selectedRecipientIds = [];
                this.updateIsAllSelectedInList();
                this.mailingListsService.refreshMailingLists();
                this.filterAllRecipients();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.mailingLists.manageDialog.confirmAction.confirmMessage.error',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.confirmAction.confirmMessage.error.detail',
                        { count }
                    )
                });
                throw err;
            }
        });
    }

    selectAllInList(): void {
        this.selectedInListIds = this.filteredRecipientsInList.map(r => r.id);
    }

    unselectAllInList(): void {
        this.selectedInListIds = [];
    }

    removeSelectedFromList(): void {
        if (!this.selectedMailingList || !this.selectedInListIds.length) {
            return;
        }

        const removeIds = new Set(this.selectedInListIds);
        const mailingList = this.selectedMailingList.clone();
        mailingList.recipientIds = this.selectedMailingList.recipientIds.filter(id => !removeIds.has(id));

        this.mailingListsService.updateMailingList(mailingList, true).subscribe({
            next: () => {
                this.selectedMailingList.recipientIds =
                    this.selectedMailingList.recipientIds.filter(id => !removeIds.has(id));
                this.recipientsInList = this.recipientsInList.filter(recipient => !removeIds.has(recipient.id));
                this.filterRecipientsInList();
                this.refreshSelectedListRecipientIds();
                this.selectedInListIds = [];

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Common.mailingLists.manageDialog.removeRecipientFromList.success.heading',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.removeRecipientsFromList.success.message',
                        { count: removeIds.size, list: this.selectedMailingList.name }
                    )
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.mailingLists.manageDialog.removeRecipientFromList.error.heading',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.removeRecipientsFromList.error.message',
                        { count: removeIds.size, list: this.selectedMailingList.name }
                    )
                });
            }
        });
    }

    addSelectedToList(): void {
        if (!this.selectedMailingList || !this.selectedRecipientIds.length) {
            return;
        }

        const newRecipientIds = this.selectedRecipientIds.filter(
            id => !this.selectedMailingList.recipientIds.includes(id)
        );
        if (!newRecipientIds.length) {
            return;
        }

        const mailingList = this.selectedMailingList.clone();
        mailingList.recipientIds = [...this.selectedMailingList.recipientIds, ...newRecipientIds];

        this.mailingListsService.updateMailingList(mailingList, false).subscribe({
            next: () => {
                this.selectedMailingList.recipientIds = mailingList.recipientIds;

                const addedRecipients = this.allRecipients.filter(r => newRecipientIds.includes(r.id));
                this.recipientsInList = [...this.recipientsInList, ...addedRecipients];
                this.filterRecipientsInList();
                this.refreshSelectedListRecipientIds();

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Common.mailingLists.manageDialog.addRecipientToList.success.heading',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.addRecipientsToList.success.message',
                        { count: newRecipientIds.length, list: this.selectedMailingList.name }
                    )
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.mailingLists.manageDialog.addRecipientToList.error.heading',
                    detail: this.localizationService.transform(
                        'Common.mailingLists.manageDialog.addRecipientsToList.error.message',
                        { count: newRecipientIds.length, list: this.selectedMailingList.name }
                    )
                });
            }
        });
    }

    private deleteInBatches(recipientIds: number[]): Observable<void> {
        const batches: number[][] = [];
        for (let i = 0; i < recipientIds.length; i += this.MAX_IDS_PER_DELETE) {
            batches.push(recipientIds.slice(i, i + this.MAX_IDS_PER_DELETE));
        }

        return from(batches).pipe(
            mergeMap(batch => this.mailingListsService.deleteRecipientsBulk(batch), 6),
            toArray(),
            map(() => void 0)
        );
    }

    private updateIsAllSelectedInList(): void {
        if (!this.selectedMailingList || !this.selectedRecipientIds.length) {
            this.isAllSelectedInList = true;
            return;
        }
        this.isAllSelectedInList = this.selectedRecipientIds.every(
            id => this.recipientIdsInList.has(id)
        );
    }
}
